import {Patient, User} from "@allaihealth/util";
import {MatTableDataSource} from "@angular/material/table";
import {AppointmentsService} from "../services/appointments.service";
import {VisibilityTimer} from "@allaihealth/ng-platform";
import {environment} from "../../environments/environment";
import {Subscription} from "rxjs";
import {AppointmentTask} from "intake-shared";
import {SurveyQuestions} from "@allaihealth/survey";

/** Helper class for managing an appointments table (used on both the index page and the appointment side nav) */
export class AppointmentTable {
	/** Data source for the table */
	dataSource:MatTableDataSource<AppointmentRow>;

	/** Interval checking if the page has gone stale */
	private updateInterval:Subscription;

	/** Subscription for new appointments being loaded from filters */
	private newAppointmentsSub:Subscription;

	private _selectedProviderIds:Set<string>;
	private _selectedStatuses:Set<string>;
	private _selectedTasks:Set<string>;
	private _currentFilter:string;

	constructor(private appointmentsService:AppointmentsService) {
	}

	connect() {

		this.newAppointmentsSub = this.appointmentsService.onNewAppointments.subscribe(() => this.refresh());

		//start the timer to check for updates
		this.updateInterval = new VisibilityTimer(environment.cacheTimeout).subscribe(
			() => this.appointmentsService.resolveAppointments(true).then()
		);
		this.refresh();
	}

	disconnect() {
		this.newAppointmentsSub?.unsubscribe();
		this.updateInterval?.unsubscribe();
		this._currentFilter = null;
	}

	public refresh() {
		//get the appointments loaded
		const appointments = this.appointmentsService.sortedAppointments;

		//convert the appointments into data for the list view
		const rows:AppointmentRow[] = appointments.flatMap(value => {
			const patient = this.appointmentsService.getPatient(value.id_patient);
			const provider = this.appointmentsService.getProvider(value.id_provider);
			const tasks = this.appointmentsService.getTasks(value.id);
			if(!patient) {
				return [];
			}

			return [{
				appointmentId: value.id,
				appointmentTime: value.appointment_date,
				patient,
				patientName: this.getPatientName(patient, patient),
				appointmentType: value.ehr_appointment_type_name,
				providerName: `${provider.first_name} ${provider.last_name}`,
				providerId: provider.id,
				provider: provider,
				tasks
			}];
		});

		//make sure to re-render the table
		this.dataSource = new MatTableDataSource<AppointmentRow>(rows);
		this.setFilterPredicate();
		this.dataSource.filter = JSON.stringify(this._selectedProviderIds) + "" + this._selectedStatuses;
	}

	private getPatientName(info:any, patient:Patient) {
		if(info?.data) {
			const pn = info.data[SurveyQuestions.PreferredName];
			if(pn?.trim().length) {
				return `${info.data[SurveyQuestions.PreferredName]} ${patient.last_name}`;
			}
		}
		return `${patient.first_name} ${patient.last_name}`;
	}

	get selectedProviderIds() {
		return this._selectedProviderIds;
	}

	set selectedProviderIds(values:Set<string>) {
		this._selectedProviderIds = values;
		this.dataSource.filter = JSON.stringify(this._selectedProviderIds);
	}

	get selectedStatuses() {
		return this._selectedStatuses;
	}

	set selectedStatuses(value:Set<string>) {
		this._selectedStatuses = value;
		this.dataSource.filter = "" + this._selectedStatuses;
	}

	get selectedTasks() {
		return this._selectedTasks;
	}

	set selectedTasks(value:Set<string>) {
		this._selectedTasks = value;
		this.dataSource.filter = "" + this._selectedTasks;
	}

	private setFilterPredicate() {
		this.dataSource.filterPredicate = (row:AppointmentRow, _filter:string) => {
			return (!this._selectedProviderIds || this._selectedProviderIds.has(row.providerId))
				&& (!this._selectedStatuses || this._selectedStatuses.has(row.tasks.status))
				&& (this.filterTasks(row))
				&& ((!this._currentFilter || this._currentFilter.trim() == "")
					|| row.patientName.toLowerCase().includes(this._currentFilter.toLowerCase())
					|| row.patient.mrn.toLowerCase().includes(this._currentFilter.toLowerCase())
					|| row.appointmentType.toLowerCase().toLowerCase().includes(this._currentFilter.toLowerCase()));
		}
	}

	private filterTasks(row:AppointmentRow) {
		if(!this._selectedTasks?.size) {
			return true;
		}
		return this.getStatusAndTypes(row).some((value:string) => this._selectedTasks.has(value));
	}

	set currentFilter(value:string) {
		this._currentFilter = value.trim().toLowerCase();
		if(this._currentFilter.trim() == "") {
			this.dataSource.filter = this.selectedProviderIds + "" + this.selectedStatuses;
		} else {
			this.dataSource.filter = this._currentFilter;
		}
	}

	private getStatusAndTypes(row:AppointmentRow) {
		const values:Set<string> = new Set();
		Object.entries(row.tasks.tasks).forEach(([key, value]) => {
			if(Object.values(value).includes(false)) {
				if(key == 'id') {
					values.add('ID');
				} else {
					values.add(key.substring(0, 1).toUpperCase() + key.substring(1));
				}
			}

			if(!Object.values(value).includes(false)) {
				values.add('Complete');
			} else if(!Object.values(value).includes(true)) {
				values.add('Not Started');
			} else {
				values.add('In Progress');
			}
		});
		return [...values];
	}
}

/** An appointment row to show in the list */
export interface AppointmentRow {
	/** The id of the appointment */
	appointmentId:string;

	/** The time the appointment starts */
	appointmentTime:number | Date;

	/** The id of the patient */
	patient:Patient;

	/** The patient's full name */
	patientName:string;

	/** The type of the appointment from the ehr */
	appointmentType?:string;

	/** The provider's full name */
	providerName?:string;

	/** The provider's id */
	providerId?:string;

	provider?:User;

	tasks?:AppointmentTask;
}
